import CloseIcon from '@mui/icons-material/Close'
import LaunchIcon from '@mui/icons-material/Launch'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined'
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo'
import { Box, Drawer, IconButton, Link, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { useSelector } from 'react-redux'
import logo from '../img/logo.svg'
import { IUser } from '../interface/IUser'

type QuickstartSidebarProps = {
    open: boolean
    onClose: () => void
}

const QuickstartSidebar = ({ open, onClose }: QuickstartSidebarProps) => {
    const { t } = useTranslation()

    const user: IUser = useSelector((state: any) => state.user?.value)
    const showWorkday = user?.worker?.mserp_usetimecard === true

    const SectionHeader = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
        <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1.25}>
            <Box sx={{ color: 'text.secondary', display: 'inline-flex' }}>{icon}</Box>
            <Typography variant="h6" component="h3">
                {title}
            </Typography>
        </Stack>
    )

    const BulletItem = ({ text }: { text: string }) => (
        <ListItem disableGutters disablePadding sx={{ py: 0.25 }}>
            <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary' }}>
                <ChevronRightIcon fontSize="small" aria-hidden />
            </ListItemIcon>
            <ListItemText primary={text} />
        </ListItem>
    )

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        width: { xs: '100%', sm: 460 },
                        p: { xs: 2, sm: 3 },
                        height: '100%',
                        overflowY: 'auto',
                    },
                },
            }}
        >
            <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1.25}>
                        <Box component="img" src={logo} alt="Time Tracking" sx={{ width: 28, height: 28 }} />
                        <Typography variant="h5" component="h2">
                            {t('quickstart.title')}
                        </Typography>
                    </Stack>
                    <IconButton onClick={onClose} aria-label={t('quickstart.close') as string}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Typography variant="body2" color="text.secondary">
                    {t('quickstart.intro')}
                </Typography>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                    <SectionHeader icon={<BoltOutlinedIcon fontSize="small" aria-hidden />} title={t('quickstart.dailyRelease.title') as string} />
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                        {t('quickstart.dailyRelease.body')}
                    </Typography>
                </Box>

                {showWorkday && (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                        <SectionHeader icon={<WorkOutlineOutlinedIcon fontSize="small" aria-hidden />} title={t('quickstart.workday.title') as string} />
                        <Typography variant="body2" sx={{ mt: 0.75 }}>
                            {t('quickstart.workday.body')}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Link href="https://wd3.myworkday.com/mercedesbenztechinnovation/d/home.htmld" target="_blank" rel="noopener" underline="hover">
                                <Stack direction="row" sx={{ alignItems: 'center' }} spacing={0.75}>
                                    <LaunchIcon fontSize="small" />
                                    <span>{t('quickstart.workday.link')}</span>
                                </Stack>
                            </Link>
                        </Box>
                    </Box>
                )}

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                    <SectionHeader icon={<CalendarMonthOutlinedIcon fontSize="small" aria-hidden />} title={t('quickstart.monthEnd.title') as string} />
                    <List dense disablePadding sx={{ mt: 0.75 }}>
                        <BulletItem text={t('quickstart.monthEnd.period1') as string} />
                        <BulletItem text={t('quickstart.monthEnd.period2') as string} />
                        <Typography variant="body2" sx={{ mt: 0.75, fontWeight: 600 }}>
                            {t('quickstart.monthEnd.note')}
                        </Typography>
                    </List>
                </Box>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                    <SectionHeader icon={<FolderOutlinedIcon fontSize="small" aria-hidden />} title={t('quickstart.projects.title') as string} />
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                        {t('quickstart.projects.body')}
                    </Typography>
                </Box>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                    <SectionHeader icon={<GavelOutlinedIcon fontSize="small" aria-hidden />} title={t('quickstart.arbzg.title') as string} />
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                        {t('quickstart.arbzg.body')}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                        <Link href="https://corpdir.sharepoint.com/sites/mbti-time-and-money/SitePages/FAQ-Arbeitszeit.aspx" target="_blank" rel="noopener" underline="hover">
                            <Stack direction="row" sx={{ alignItems: 'center' }} spacing={0.75}>
                                <LaunchIcon fontSize="small" />
                                <span>{t('quickstart.arbzg.link')}</span>
                            </Stack>
                        </Link>
                    </Box>
                </Box>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                    <SectionHeader icon={<AutoAwesomeOutlinedIcon fontSize="small" aria-hidden />} title={t('quickstart.features.title') as string} />
                    <Box sx={{ mt: 1.25 }}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 'bold' }}>
                            <ContentPasteGoIcon fontSize="small" aria-hidden />
                            <span>{t('quickstart.features.templatestitle')}</span>
                        </Typography>
                        <List dense disablePadding sx={{ mt: 0.75 }}>
                            <BulletItem text={t('quickstart.features.templates') as string} />
                        </List>
                    </Box>
                    <Box sx={{ mt: 1.25 }}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 'bold' }}>
                            <KeyboardOutlinedIcon fontSize="small" aria-hidden />
                            <span>{t('quickstart.shortcuts.title')}</span>
                        </Typography>
                        <List dense disablePadding sx={{ mt: 0.75 }}>
                            <BulletItem text={t('quickstart.shortcuts.items.addLine') as string} />
                            <BulletItem text={t('quickstart.shortcuts.items.save') as string} />
                            <BulletItem text={t('quickstart.shortcuts.items.release') as string} />
                            <BulletItem text={t('quickstart.shortcuts.items.reopen') as string} />
                        </List>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <span>{t('quickstart.shortcuts.note')}</span>
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                    <SectionHeader icon={<HelpOutlineOutlinedIcon fontSize="small" aria-hidden />} title={t('quickstart.helpcenter.title') as string} />
                    <Box sx={{ mt: 1 }}>
                        <Link href="https://corpdir.sharepoint.com/sites/mbti-apps-and-tools/SitePages/Navision.aspx#hilfeartikel" target="_blank" rel="noopener" underline="hover">
                            <Stack direction="row" sx={{ alignItems: 'center' }} spacing={0.75}>
                                <LaunchIcon fontSize="small" />
                                <span>{t('quickstart.helpcenter.link')}</span>
                            </Stack>
                        </Link>
                    </Box>
                </Box>

                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                    <SectionHeader icon={<RestartAltOutlinedIcon fontSize="small" aria-hidden />} title={t('quickstart.reopen.title') as string} />
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                        {t('quickstart.reopen.body')}
                    </Typography>
                </Box>
            </Stack>
        </Drawer>
    )
}

export default QuickstartSidebar
